import React, { memo, useEffect, useRef, useState } from "react";
import { shallowEqual } from "react-redux";
import { toast } from "react-toastify";
import { useTranslation } from "react-i18next";
import { RootState } from "typedefs";
import { useActions, useSelector } from "app/utils/hooks";
import {
  fetchCoachProfile,
  updateCoachProfile,
} from "app/actions/coachNowCoachProfile";
import { editProfileSettings, fetchSelf } from "app/actions/user";
import { CoachProfileModel } from "app/models/CoachProfileModel";
import AcademyWizardLayout from "app/components/Layouts/AcademyWizardLayout";
import AvatarField from "app/routes/AcademyWizard/_components/AvatarField";
import WizardTextField from "app/routes/AcademyWizard/_components/WizardTextField";
import { Loading } from "app/components/Wrappers";
import upload from "app/utils/uploader";
import {
  NAME_MAX_LENGTH,
  validateFirstName,
  validateLastName,
} from "app/routes/AcademyWizard/validation";
import { initialsOf } from "app/routes/AcademyWizard/api";
import styles from "./styles.module.scss";

// The avatar lives on the MAIN account record; coach_profiles/:id only mirrors
// it, serving a CloudFront ".../default-..." fallback image when none is set.
// Same "default" sentinel check /account/edit uses to tell the two apart.
const realAvatar = (url?: string | null): string | null =>
  url && !String(url).includes("default") ? url : null;

const AcademyWizardCoachProfile = () => {
  const { t } = useTranslation();
  const { self } = useSelector((state: RootState) => state.user, shallowEqual);
  const actions = useActions({
    fetchCoachProfile,
    updateCoachProfile,
    editProfileSettings,
    fetchSelf,
  });

  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [about, setAbout] = useState("");
  const [touched, setTouched] = useState({
    firstName: false,
    lastName: false,
  });
  const [loading, setLoading] = useState(true);
  const [avatarUrl, setAvatarUrl] = useState<string | null>(null);
  // Attachment id from the upload — what PUT /api/v1/account persists.
  const [avatarId, setAvatarId] = useState<string | null>(null);
  const [removeAvatar, setRemoveAvatar] = useState(false);
  const [uploadingAvatar, setUploadingAvatar] = useState(false);
  // The upload in flight, if any: Next awaits it so a photo picked right
  // before advancing is saved rather than orphaned.
  const pendingUpload = useRef<ReturnType<typeof upload> | null>(null);
  // The fetched profile is kept whole so the PUT can merge the edited fields
  // over untouched ones (phone, address, links) instead of blanking them.
  const profileRef = useRef<CoachProfileModel | null>(null);

  useEffect(() => {
    if (!self?.id) {
      return undefined;
    }
    let isMounted = true;
    actions
      .fetchCoachProfile(self.id)
      .then((profile: CoachProfileModel) => {
        if (!isMounted || !profile) {
          return;
        }
        profileRef.current = profile;
        setFirstName(profile.firstName || "");
        setLastName(profile.lastName || "");
        setAbout(profile.about || "");
        setAvatarUrl(realAvatar(profile.avatarUrl));
      })
      .catch((error) => {
        console.error("Error loading coach profile:", error);
        if (isMounted) {
          toast.error(t("Something went wrong."));
        }
      })
      .finally(() => {
        if (isMounted) {
          setLoading(false);
        }
      });
    return () => {
      isMounted = false;
    };
  }, [actions, self?.id]);

  const touch = (field: keyof typeof touched) => () =>
    setTouched((prev) => ({ ...prev, [field]: true }));

  const firstNameError = touched.firstName
    ? validateFirstName(firstName)
    : null;
  const lastNameError = touched.lastName ? validateLastName(lastName) : null;

  const onUploadAvatar = async (file: File) => {
    setUploadingAvatar(true);
    const request = upload(file, file.name, "avatar", "avatar");
    pendingUpload.current = request;
    try {
      const result = await request;
      setAvatarUrl(result.url);
      setAvatarId(result.id);
      setRemoveAvatar(false);
    } catch (error) {
      console.error("Error uploading file:", error);
      toast.error(t("Something went wrong."));
    } finally {
      if (pendingUpload.current === request) {
        pendingUpload.current = null;
      }
      setUploadingAvatar(false);
    }
  };

  const onDeleteAvatar = () => {
    setAvatarUrl(null);
    setAvatarId(null);
    setRemoveAvatar(true);
  };

  // Reveals errors on fields the coach never blurred, then PUTs the merged
  // profile the same way /account/coach-profile's Save Changes does.
  const onBeforeNext = async () => {
    setTouched({ firstName: true, lastName: true });
    const valid = !validateFirstName(firstName) && !validateLastName(lastName);
    if (!valid) {
      return false;
    }
    // A photo still uploading is awaited and read from the result: the
    // avatarId in this closure predates its setAvatarId. A failed upload
    // has already toasted and saves nothing.
    const uploaded = pendingUpload.current
      ? await pendingUpload.current.then(
          (result) => result,
          () => null,
        )
      : null;
    if (!profileRef.current) {
      // No fetched profile → nothing to merge the PUT onto. Thrown (not
      // `false`) so the layout's catch shows its error toast; the fields are
      // valid, so there is nothing on screen explaining a dead Next button.
      throw new Error("No coach profile to save the Coach Profile step into");
    }
    const payload: CoachProfileModel & Record<string, any> = {
      ...profileRef.current,
      firstName: firstName.trim(),
      lastName: lastName.trim(),
      about: about.trim(),
    };

    // The avatar persists on the main account (PUT /api/v1/account, the same
    // avatar_id/remove_avatar contract /account/edit uses) — writing it to the
    // coach profile is a no-op, coach_profiles/:id only mirrors the account.
    const initialAvatar = realAvatar(profileRef.current.avatarUrl);
    const savedAvatarId = uploaded ? uploaded.id : avatarId;
    const savedAvatarUrl = uploaded ? uploaded.url : avatarUrl;
    let saveAvatar: Promise<any> = Promise.resolve();
    if (savedAvatarId && savedAvatarUrl && savedAvatarUrl !== initialAvatar) {
      saveAvatar = actions.editProfileSettings({
        avatar_id: savedAvatarId,
        remove_avatar: false,
      });
    } else if (!uploaded && removeAvatar && initialAvatar) {
      saveAvatar = actions.editProfileSettings({
        avatar_id: null,
        remove_avatar: true,
      });
    }

    return Promise.all([actions.updateCoachProfile(payload), saveAvatar]).then(
      async () => {
        // Refresh the mirrored avatar in redux; failing to is only staleness,
        // never a reason to hold the coach on the step. Forced: without it
        // fetchSelf is a no-op once scheduling data is loaded, so the sidebar
        // kept the old avatar (or initials) until a hard reload.
        try {
          await actions.fetchSelf(true);
        } catch (error) {
          console.error("Error refreshing account:", error);
        }
        return true;
      },
    );
  };

  // Field counts for the academy_wizard_step_completed event: completed =
  // non-empty now, prefilled = non-empty as fetched, edited = differs from the
  // fetched value.
  const collectStepStats = () => {
    const profile = profileRef.current;
    const values = [firstName, lastName, about, avatarUrl || ""];
    const initialValues = profile
      ? [
          profile.firstName || "",
          profile.lastName || "",
          profile.about || "",
          realAvatar(profile.avatarUrl) || "",
        ]
      : [];
    return {
      completed: values.filter((value) => value && value.trim()).length,
      prefilled: initialValues.filter((value) => value && value.trim()).length,
      edited: profile
        ? values.filter((value, index) => value !== initialValues[index]).length
        : 0,
    };
  };

  const initials =
    initialsOf(`${firstName} ${lastName}`) || initialsOf(self?.displayName || "");

  return (
    <AcademyWizardLayout
      stepPath="/academy/wizard/coach-profile"
      title={t("Tell us about yourself")}
      subtitle={t(
        "A public booking page you can share anywhere — and the profile your customers see when they connect with you on CoachNow.",
      )}
      onBeforeNext={onBeforeNext}
      loading={loading}
      nextLabel={t("Take me to my Account")}
      collectStepStats={collectStepStats}
    >
      {loading ? (
        <Loading isLoading loadType="spinner" />
      ) : (
        <div className={styles.fields}>
          <AvatarField
            initials={initials}
            imageUrl={avatarUrl}
            uploading={uploadingAvatar}
            onUpload={onUploadAvatar}
            onDelete={onDeleteAvatar}
          />
          <div className={styles.row}>
            <div className={styles.row_item}>
              <WizardTextField
                label={t("First Name *")}
                required
                placeholder={t("First Name")}
                value={firstName}
                maxLength={NAME_MAX_LENGTH}
                error={firstNameError ? t(firstNameError) : undefined}
                onChange={setFirstName}
                onBlur={touch("firstName")}
              />
            </div>
            <div className={styles.row_item}>
              <WizardTextField
                label={t("Last Name *")}
                required
                placeholder={t("Last Name")}
                value={lastName}
                maxLength={NAME_MAX_LENGTH}
                error={lastNameError ? t(lastNameError) : undefined}
                onChange={setLastName}
                onBlur={touch("lastName")}
              />
            </div>
          </div>
          <WizardTextField
            label={t("Tell athletes about yourself (optional)")}
            placeholder={t("You can edit this later.")}
            value={about}
            multiline
            onChange={setAbout}
          />
        </div>
      )}
    </AcademyWizardLayout>
  );
};

export default memo(AcademyWizardCoachProfile);
